import React, { useMemo } from 'react';
import { BankFileConversionType, Box, FileConversionService, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { Button, UtilButton, Complete } from '../../UI';
import styles from './ConvertMyTemplateConfirm.module.scss';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useDialog } from '@/providers/Dialog.provider';
import ProcessFileDialog from '../FileUploadHistory/ProcessFileDialog/ProcessFileDialog';
import ProcessFileSuccessDialog from '../FileUploadHistory/ProcessFileDialog/ProcessFileSuccessDialog';
import { type ProcessFileSuccessResult } from '../FileUploadHistory/ProcessFileDialog/ProcessFileDialog';
import DownloadFileDialog from '../FileUploadHistory/DownloadFileDialog/DownloadFileDialog';
import { DownloadFileFormValues } from '../FileUploadHistory/DownloadFileDialog/useDownloadFileFormFields';
import { useFileConfigurations } from '../FileConfigurationProvider';
import { useClientData } from '@/providers/ClientData.provider';

const classes = withStyles(styles);

const ConvertMyTemplateConfirm: React.FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.ConvertMyTemplateConfirm' });
  const { t: tHeader } = useTranslation('translations', { keyPrefix: 'FileUpload.ConvertMyTemplateConfirm.Header' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const {
    response: { Country },
  } = useClientData();
  const location = useLocation();
  const history = useHistory();
  const { downloadFile } = useFileConversion();
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const dialog = useDialog();
  const { configurations, configurationsLoading } = useFileConfigurations();

  // Get details from query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      transferType: params.get('transferType') || '',
      currency: params.get('currency') || (Country === 'US' ? 'USD' : 'CAD'),
      uploadFile: params.get('uploadFile') || '',
      numberOfTransactions: parseInt(params.get('numberOfTransactions') || '0'),
      totalAmount: params.get('totalAmount'),
      convertToBank: params.get('convertToBank') || '',
      fileConversionID: params.get('fileConversionID') || '',
      service: (params.get('service') || '') as FileConversionService,
    };
  }, [location.search]);

  const details = useMemo(
    () => [
      {
        label: tHeader('transferType', { defaultValue: 'Transfer Type' }),
        value: tGlobal(`ServiceOptions.${queryParams.transferType}`),
      },
      {
        label: tHeader('uploadFile', { defaultValue: 'Upload File' }),
        value: queryParams.uploadFile,
      },
      {
        label: tHeader('numberOfTransactions', { defaultValue: 'Number of Transactions' }),
        value: queryParams.numberOfTransactions,
      },
      {
        label: tHeader('currency', { defaultValue: 'Currency' }),
        value: queryParams.currency,
      },
      {
        label: tHeader('totalAmount', { defaultValue: 'Total Amount' }),
        value: `$${queryParams.totalAmount}`,
      },
    ],
    [queryParams],
  );

  const handleDownloadFile = async (fileConversionID: string, res: DownloadFileFormValues) => {
    try {
      setLoading(true);
      await downloadFile({
        FileConversionID: fileConversionID,
        FileConversionConfigurationID: res.FileConversionConfigurationID,
        PaymentRail: res.PaymentRail || undefined,
        ValueDate: res.ValueDate,
      });
    } catch (err) {
      alertDialog.error(t('downloadErrorTitle'), t('downloadError', { defaultValue: 'Submission Error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDialog = async (fileConversionID: string, service: FileConversionService) => {
    const res = await dialog.open<DownloadFileFormValues>(({ onCancel, onConfirm }) => (
      <DownloadFileDialog
        onCancel={onCancel}
        onConfirm={onConfirm}
        configurations={configurations}
        configurationsLoading={configurationsLoading}
        service={service}
      />
    ));

    if (!res) return;
    handleDownloadFile(fileConversionID, res);
  };

  const handleProcessDialog = async (fileConversionID: string, service: FileConversionService) => {
    const result = await dialog.open<ProcessFileSuccessResult>(({ onCancel, onConfirm }) => (
      <ProcessFileDialog
        onCancel={onCancel}
        onConfirm={onConfirm}
        fileConversionID={fileConversionID}
        configurations={configurations}
        configurationsLoading={configurationsLoading}
        service={service}
      />
    ));

    if (!result) return;

    // Show success dialog with the result
    await dialog.open(({ onCancel }) => <ProcessFileSuccessDialog onClose={onCancel} data={result} />);
    history.push('/file-upload-history?tab=fileConversion');
  };
  const canDownload = useMemo(() => {
    const options = (configurations || [])
      .map((config) => ({
        value: config.Processor,
        label: config.FinancialInstitution,
      }))
      .filter((option): option is { value: BankFileConversionType; label: string } => option !== null);

    if (options.length === 1 && options[0].value === 'default_processor') {
      return false;
    }
    return options.length > 0;
  }, [configurations]);
  return (
    <div className={classes('container')}>
      <div className={classes('completeWrapper')}>
        <Complete
          title={t('title', { defaultValue: 'File uploaded successfully' })}
          showAmounts={false}
          details={details}
          header={
            <div className={classes('header')}>
              <UtilButton
                className={classes('homeButton')}
                iconLeft="chevron_left"
                size="large"
                onClick={() => history.push('/file-upload-history?tab=fileConversion')}
              >
                {t('home', { defaultValue: 'Home' })}
              </UtilButton>
            </div>
          }
        >
          <Box gap="md" width="100%" jc="sb" mt="sm">
            <Button
              onClick={() => {
                const isLocked = !lockedFeatures.FileSubmissionEnabled;
                if (isLocked) {
                  updateIsLocked(true, t('fileSubmission', { defaultValue: 'File Submission' }));
                } else {
                  handleProcessDialog(queryParams.fileConversionID, queryParams.service);
                }
              }}
              className={classes('button')}
              size="small"
              colorScheme="secondary"
              iconRight={!lockedFeatures.FileSubmissionEnabled ? 'lock' : undefined}
            >
              {t('processTransactions', { defaultValue: 'Process Transactions' })}
            </Button>
            {canDownload && (
              <Button
                colorScheme="primary"
                onClick={() => handleDownloadDialog(queryParams.fileConversionID, queryParams.service)}
                className={classes('button')}
                size="small"
              >
                {t('downloadConvertedFile', { defaultValue: 'Download Converted File' })}
              </Button>
            )}
          </Box>
        </Complete>
      </div>
    </div>
  );
};

export default ConvertMyTemplateConfirm;
